export type LeagueKey = 'nba' | 'cba' | 'international';

export type MatchStatus = 'live' | 'upcoming' | 'finished';

export interface TeamInfo {
  name: string;
  shortName: string;
  record: string;
  score: number;
  quarters: number[];
  starters: string[];
  coach: string;
}

export interface MatchStats {
  label: string;
  home: string;
  away: string;
  homeValue: number;
  awayValue: number;
}

export interface Match {
  id: string;
  league: LeagueKey;
  leagueLabel: string;
  round: string;
  home: TeamInfo;
  away: TeamInfo;
  startTime: string;
  dateLabel: string;
  timeLabel: string;
  status: MatchStatus;
  progress: number;
  quarter: string;
  clock: string;
  venue: string;
  broadcaster: string;
  views: string;
  summary: string;
  cover: string;
  stats: MatchStats[];
  highlights: string[];
}

export const leagueMeta: Record<
  LeagueKey,
  { key: LeagueKey; name: string; shortName: string; intro: string; icon: string }
> = {
  nba: {
    key: 'nba',
    name: 'NBA',
    shortName: 'NBA',
    intro:
      '美国职业篮球联赛，汇聚全球顶尖球星与最具观赏性的进攻篮球。常规赛、季后赛与总决赛全程追踪，湖人、勇士、凯尔特人、掘金等豪门对决一网打尽。',
    icon: 'ri-basketball-line',
  },
  cba: {
    key: 'cba',
    name: 'CBA',
    shortName: 'CBA',
    intro:
      '中国男子篮球职业联赛，本土球员与外援同场竞技。辽宁、广东、浙江、上海等球队的焦点战役与季后赛争夺，尽在篮球直播。',
    icon: 'ri-team-line',
  },
  international: {
    key: 'international',
    name: '国际赛',
    shortName: 'FIBA',
    intro:
      '涵盖 FIBA 世界杯、欧洲篮球锦标赛、奥运会篮球赛事及亚洲杯等国际赛事。国家队层面的荣誉之争，节奏更快、对抗更强。',
    icon: 'ri-global-line',
  },
};

export const leagueOrder: LeagueKey[] = ['nba', 'cba', 'international'];

export const statusMeta: Record<MatchStatus, { label: string; dot: string }> = {
  live: { label: '直播中', dot: 'bg-accent-500' },
  upcoming: { label: '即将开始', dot: 'bg-secondary-500' },
  finished: { label: '已结束', dot: 'bg-foreground-400' },
};

export const matches: Match[] = [
  {
    id: 'nba-lal-gsw-20241025',
    league: 'nba',
    leagueLabel: 'NBA',
    round: 'NBA 2024-25 常规赛 · 第 4 轮',
    home: {
      name: '洛杉矶湖人',
      shortName: 'LAL',
      record: '3 胜 1 负',
      score: 88,
      quarters: [26, 22, 24, 16],
      starters: ['勒布朗·詹姆斯（前锋）', '安东尼·戴维斯（中锋）', '奥斯汀·里夫斯（后卫）', '德安吉洛·拉塞尔（后卫）', '八村塁（前锋）'],
      coach: 'JJ·雷迪克',
    },
    away: {
      name: '金州勇士',
      shortName: 'GSW',
      record: '2 胜 2 负',
      score: 94,
      quarters: [24, 28, 21, 21],
      starters: ['斯蒂芬·库里（后卫）', '安德鲁·威金斯（前锋）', '德雷蒙德·格林（前锋）', '乔纳森·库明加（前锋）', '巴迪·希尔德（后卫）'],
      coach: '史蒂夫·科尔',
    },
    startTime: '2024-10-26T10:30:00+08:00',
    dateLabel: '今日 10:30',
    timeLabel: '今日 10:30',
    status: 'live',
    progress: 68,
    quarter: '第四节 06:42',
    clock: '距结束 06:42',
    venue: 'Crypto.com 竞技场',
    broadcaster: '篮球直播 · 高清 1 号机位',
    views: '128.6 万',
    summary:
      '西部传统强队的正面碰撞。湖人依靠内线高度与转换快攻建立优势，勇士则用外线三分与无球跑动紧咬比分。第四节中段双方仅差 6 分，库里已经砍下 29 分，詹姆斯贡献 24 分 8 助攻。',
    cover:
      'https://readdy.ai/api/search-image?query=dynamic%20basketball%20game%20scene%20with%20abstract%20geometric%20orange%20and%20navy%20light%20streaks%20over%20a%20dark%20court%20with%20motion%20blur%20smoke%20stylized%20sports%20poster%20art%20no%20text&width=1600&height=900&seq=bb-lal-gsw-hero-01&orientation=landscape',
    stats: [
      { label: '投篮命中率', home: '49.2%', away: '51.8%', homeValue: 49, awayValue: 52 },
      { label: '三分命中', home: '9 / 26', away: '14 / 33', homeValue: 35, awayValue: 42 },
      { label: '篮板', home: '38', away: '31', homeValue: 55, awayValue: 45 },
      { label: '助攻', home: '21', away: '26', homeValue: 45, awayValue: 55 },
      { label: '失误', home: '11', away: '9', homeValue: 45, awayValue: 55 },
    ],
    highlights: ['库里第三节连中 4 记三分', '詹姆斯末节开节独得 7 分', '戴维斯封盖库明加的快攻'],
  },
  {
    id: 'cba-liaoning-guangdong-20241026',
    league: 'cba',
    leagueLabel: 'CBA',
    round: 'CBA 2024-25 常规赛 · 第 8 轮',
    home: {
      name: '辽宁本钢',
      shortName: 'LNB',
      record: '7 胜 0 负',
      score: 0,
      quarters: [],
      starters: ['赵继伟（后卫）', '张镇麟（前锋）', '付豪（前锋）', '李晓旭（前锋）', '弗格（后卫）'],
      coach: '杨鸣',
    },
    away: {
      name: '广东华南虎',
      shortName: 'GD',
      record: '5 胜 2 负',
      score: 0,
      quarters: [],
      starters: ['胡明轩（后卫）', '徐杰（后卫）', '任骏飞（前锋）', '杜润旺（前锋）', '贝兹利（前锋）'],
      coach: '杜锋',
    },
    startTime: '2024-10-26T19:35:00+08:00',
    dateLabel: '今日 19:35',
    timeLabel: '今日 19:35',
    status: 'upcoming',
    progress: 0,
    quarter: '未开始',
    clock: '今晚 19:35 开赛',
    venue: '沈阳奥体中心体育馆',
    broadcaster: '篮球直播 · 官方信号',
    views: '预约 84.2 万',
    summary:
      'CBA 最具话题度的南北对话。辽宁本赛季开局豪取七连胜，内线厚度与转换速度兼具；广东则依靠后卫线的快速推进与外线牵制找回节奏。赵继伟与胡明轩的对位是全场焦点。',
    cover:
      'https://readdy.ai/api/search-image?query=abstract%20indoor%20basketball%20arena%20with%20orange%20spotlight%20beams%20and%20deep%20blue%20crowd%20silhouettes%20stylized%20editorial%20sports%20poster%20no%20text&width=1600&height=900&seq=bb-lnb-gd-hero-02&orientation=landscape',
    stats: [
      { label: '场均得分', home: '112.4', away: '108.6', homeValue: 52, awayValue: 48 },
      { label: '场均篮板', home: '46.2', away: '41.8', homeValue: 55, awayValue: 45 },
      { label: '场均助攻', home: '24.1', away: '26.7', homeValue: 45, awayValue: 55 },
      { label: '三分命中率', home: '36.8%', away: '34.2%', homeValue: 53, awayValue: 47 },
    ],
    highlights: ['辽宁主场本赛季保持全胜', '广东后卫线场均 38 分', '两队近 10 次交手广东 6 胜 4 负'],
  },
  {
    id: 'fiba-usa-serbia-20241024',
    league: 'international',
    leagueLabel: '国际赛',
    round: 'FIBA 世界杯热身赛 · 焦点战',
    home: {
      name: '美国男篮',
      shortName: 'USA',
      record: '国际赛 5 胜 0 负',
      score: 0,
      quarters: [],
      starters: ['安东尼·爱德华兹（后卫）', '泰瑞斯·哈利伯顿（后卫）', '杰森·塔图姆（前锋）', '班凯罗（前锋）', '巴姆·阿德巴约（中锋）'],
      coach: '史蒂夫·科尔',
    },
    away: {
      name: '塞尔维亚男篮',
      shortName: 'SRB',
      record: '国际赛 4 胜 1 负',
      score: 0,
      quarters: [],
      starters: ['博格丹·博格达诺维奇（后卫）', '尼古拉·约维奇（前锋）', '阿列克萨·阿夫拉莫维奇（后卫）', '菲利普·彼得鲁舍夫（中锋）', '马尔科·古杜里奇（前锋）'],
      coach: '斯维蒂斯拉夫·佩西奇',
    },
    startTime: '2024-10-27T03:00:00+08:00',
    dateLabel: '明日 03:00',
    timeLabel: '明日 03:00',
    status: 'upcoming',
    progress: 0,
    quarter: '未开始',
    clock: '明日 03:00 开赛',
    venue: '马德里体育中心',
    broadcaster: '篮球直播 · 国际信号',
    views: '预约 46.8 万',
    summary:
      'FIBA 体系下战术最讲究的两支球队。美国队依靠运动能力与深度轮换压制对手，塞尔维亚则以精确的团队传导与高位策应撕开防线。三分线与篮板球的争夺将决定比赛走向。',
    cover:
      'https://readdy.ai/api/search-image?query=stylized%20international%20basketball%20game%20artwork%20with%20orange%20and%20navy%20colored%20light%20ribbons%20and%20a%20glossy%20basketball%20floating%20above%20a%20dark%20floor%20minimal%20modern%20sports%20design%20no%20text&width=1600&height=900&seq=bb-usa-srb-hero-03&orientation=landscape',
    stats: [
      { label: '场均得分', home: '98.6', away: '91.2', homeValue: 54, awayValue: 46 },
      { label: '场均篮板', home: '44.0', away: '39.4', homeValue: 56, awayValue: 44 },
      { label: '场均助攻', home: '25.8', away: '23.4', homeValue: 52, awayValue: 48 },
      { label: '三分命中率', home: '39.1%', away: '37.6%', homeValue: 52, awayValue: 48 },
    ],
    highlights: ['美国队热身赛保持不败', '塞尔维亚半场阵地进攻效率第一', '两队上次交手美国 4 分险胜'],
  },
  {
    id: 'nba-bos-nyk-20241024',
    league: 'nba',
    leagueLabel: 'NBA',
    round: 'NBA 2024-25 常规赛 · 第 3 轮',
    home: {
      name: '波士顿凯尔特人',
      shortName: 'BOS',
      record: '4 胜 0 负',
      score: 112,
      quarters: [30, 26, 28, 28],
      starters: ['杰森·塔图姆（前锋）', '杰伦·布朗（前锋）', '德里克·怀特（后卫）', '朱·霍勒迪（后卫）', '克里斯塔普斯·波尔津吉斯（中锋）'],
      coach: '乔·马祖拉',
    },
    away: {
      name: '纽约尼克斯',
      shortName: 'NYK',
      record: '2 胜 2 负',
      score: 105,
      quarters: [25, 27, 26, 27],
      starters: ['杰伦·布伦森（后卫）', '米卡尔·布里奇斯（前锋）', 'OG·阿努诺比（前锋）', '卡尔·安东尼·唐斯（中锋）', '乔什·哈特（后卫）'],
      coach: '汤姆·锡伯杜',
    },
    startTime: '2024-10-23T07:30:00+08:00',
    dateLabel: '10月23日 07:30',
    timeLabel: '10月23日 07:30',
    status: 'finished',
    progress: 100,
    quarter: '已结束',
    clock: '终场 112 - 105',
    venue: 'TD 花园球馆',
    broadcaster: '篮球直播 · 录播回看',
    views: '96.3 万',
    summary:
      '凯尔特人依靠第三节的 12-2 攻击波拉开分差，塔图姆全场 32 分 9 篮板，霍勒迪末节连续两次抢断锁定胜局。尼克斯虽然内线占据优势，但三分命中率仅 29%。',
    cover:
      'https://readdy.ai/api/search-image?query=abstract%20basketball%20arena%20at%20night%20with%20deep%20navy%20tone%20and%20warm%20orange%20rim%20lights%20reflecting%20on%20a%20polished%20wood%20court%20minimal%20editorial%20sports%20art%20no%20text&width=1600&height=900&seq=bb-bos-nyk-hero-04&orientation=landscape',
    stats: [
      { label: '投篮命中率', home: '51.2%', away: '44.6%', homeValue: 53, awayValue: 46 },
      { label: '三分命中', home: '16 / 40', away: '9 / 31', homeValue: 58, awayValue: 42 },
      { label: '篮板', home: '44', away: '49', homeValue: 47, awayValue: 53 },
      { label: '助攻', home: '28', away: '24', homeValue: 54, awayValue: 46 },
    ],
    highlights: ['塔图姆砍下 32 分 9 篮板', '霍勒迪末节两次关键抢断', '尼克斯三分命中率仅 29%'],
  },
  {
    id: 'cba-zhejiang-shanghai-20241022',
    league: 'cba',
    leagueLabel: 'CBA',
    round: 'CBA 2024-25 常规赛 · 第 7 轮',
    home: {
      name: '浙江稠州金租',
      shortName: 'ZJ',
      record: '5 胜 2 负',
      score: 121,
      quarters: [32, 29, 30, 30],
      starters: ['程帅澎（后卫）', '吴前（后卫）', '王奕博（后卫）', '朱俊龙（前锋）', '盖利（中锋）'],
      coach: '王世龙',
    },
    away: {
      name: '上海久事',
      shortName: 'SHA',
      record: '2 胜 5 负',
      score: 98,
      quarters: [22, 26, 24, 26],
      starters: ['王哲林（中锋）', '李添荣（后卫）', '刘铮（前锋）', '郭昊文（前锋）', '威尔逊（后卫）'],
      coach: '内特·麦克米兰',
    },
    startTime: '2024-10-21T19:35:00+08:00',
    dateLabel: '10月21日 19:35',
    timeLabel: '10月21日 19:35',
    status: 'finished',
    progress: 100,
    quarter: '已结束',
    clock: '终场 121 - 98',
    venue: '杭州体育馆',
    broadcaster: '篮球直播 · 录播回看',
    views: '61.4 万',
    summary:
      '浙江全场命中 18 记三分，第三节单节净胜 12 分直接带走比赛。吴前得到 27 分 8 助攻，上海尽管王哲林贡献 26 分 13 篮板，但外线失准导致追分乏力。',
    cover:
      'https://readdy.ai/api/search-image?query=abstract%20orange%20concentric%20basketball%20court%20lines%20over%20a%20misty%20navy%20background%20with%20soft%20light%20haze%20clean%20modern%20sports%20graphic%20no%20text&width=1600&height=900&seq=bb-zj-sh-hero-05&orientation=landscape',
    stats: [
      { label: '投篮命中率', home: '54.1%', away: '43.8%', homeValue: 55, awayValue: 45 },
      { label: '三分命中', home: '18 / 42', away: '7 / 30', homeValue: 60, awayValue: 40 },
      { label: '篮板', home: '41', away: '45', homeValue: 48, awayValue: 52 },
      { label: '助攻', home: '29', away: '19', homeValue: 60, awayValue: 40 },
    ],
    highlights: ['吴前 27 分 8 助攻', '浙江全场 18 记三分', '王哲林 26 分 13 篮板空砍'],
  },
  {
    id: 'fiba-euro-fra-esp-20241020',
    league: 'international',
    leagueLabel: '国际赛',
    round: '欧洲篮球锦标赛 · 小组赛',
    home: {
      name: '法国男篮',
      shortName: 'FRA',
      record: '小组 2 胜 0 负',
      score: 84,
      quarters: [20, 22, 19, 23],
      starters: ['维克托·文班亚马（中锋）', '尼利基纳（后卫）', '巴图姆（前锋）', '库利巴利（前锋）', '戈贝尔（中锋）'],
      coach: '文森特·科莱',
    },
    away: {
      name: '西班牙男篮',
      shortName: 'ESP',
      record: '小组 1 胜 1 负',
      score: 79,
      quarters: [18, 21, 22, 18],
      starters: ['威利·埃尔南戈麦斯（中锋）', '鲁迪·费尔南德斯（前锋）', '洛伦佐·布朗（后卫）', '胡安·努涅斯（后卫）', '阿尔达马（前锋）'],
      coach: '塞尔吉奥·斯卡里奥洛',
    },
    startTime: '2024-10-19T22:00:00+08:00',
    dateLabel: '10月19日 22:00',
    timeLabel: '10月19日 22:00',
    status: 'finished',
    progress: 100,
    quarter: '已结束',
    clock: '终场 84 - 79',
    venue: '柏林梅赛德斯奔驰体育馆',
    broadcaster: '篮球直播 · 录播回看',
    views: '38.2 万',
    summary:
      '文班亚马拿下 24 分 11 篮板 4 盖帽，末节最后两分钟连得 6 分帮助法国险胜。西班牙全场三分 12 中 8，但篮板以 28-39 落后。',
    cover:
      'https://readdy.ai/api/search-image?query=stylized%20european%20basketball%20arena%20interior%20with%20warm%20amber%20light%20and%20deep%20blue%20stands%20soft%20fog%20minimal%20poster%20composition%20no%20text&width=1600&height=900&seq=bb-fra-esp-hero-06&orientation=landscape',
    stats: [
      { label: '投篮命中率', home: '48.6%', away: '46.2%', homeValue: 51, awayValue: 49 },
      { label: '三分命中', home: '8 / 24', away: '12 / 30', homeValue: 44, awayValue: 56 },
      { label: '篮板', home: '39', away: '28', homeValue: 58, awayValue: 42 },
      { label: '助攻', home: '20', away: '23', homeValue: 47, awayValue: 53 },
    ],
    highlights: ['文班亚马 24 分 11 篮板 4 盖帽', '法国篮板优势 39-28', '末节最后 2 分钟法国连得 6 分'],
  },
  {
    id: 'nba-den-mia-20241025',
    league: 'nba',
    leagueLabel: 'NBA',
    round: 'NBA 2024-25 常规赛 · 第 4 轮',
    home: {
      name: '丹佛掘金',
      shortName: 'DEN',
      record: '2 胜 2 负',
      score: 0,
      quarters: [],
      starters: ['尼古拉·约基奇（中锋）', '贾马尔·穆雷（后卫）', '迈克尔·波特（前锋）', '阿隆·戈登（前锋）', '克里斯蒂安·布劳恩（后卫）'],
      coach: '迈克尔·马龙',
    },
    away: {
      name: '迈阿密热火',
      shortName: 'MIA',
      record: '3 胜 1 负',
      score: 0,
      quarters: [],
      starters: ['吉米·巴特勒（前锋）', '班姆·阿德巴约（中锋）', '泰勒·希罗（后卫）', '特里·罗齐尔（后卫）', '海梅·哈克斯（前锋）'],
      coach: '埃里克·斯波尔斯特拉',
    },
    startTime: '2024-10-26T08:00:00+08:00',
    dateLabel: '今日 08:00',
    timeLabel: '今日 08:00',
    status: 'upcoming',
    progress: 0,
    quarter: '未开始',
    clock: '今早 08:00 开赛',
    venue: '波尔球馆',
    broadcaster: '篮球直播 · 高清 2 号机位',
    views: '预约 58.1 万',
    summary:
      '约基奇与阿德巴约的内线策应大战。掘金依靠约基奇的高位手递手创造空位，热火则用换防与区域联防限制对手的二次进攻。两队近三个赛季交手互有胜负。',
    cover:
      'https://readdy.ai/api/search-image?query=dark%20navy%20basketball%20court%20with%20orange%20glowing%20outline%20and%20soft%20spotlight%20haze%20minimal%20abstract%20sports%20poster%20no%20text&width=1600&height=900&seq=bb-den-mia-hero-07&orientation=landscape',
    stats: [
      { label: '场均得分', home: '110.2', away: '106.8', homeValue: 51, awayValue: 49 },
      { label: '场均篮板', home: '47.6', away: '43.1', homeValue: 53, awayValue: 47 },
      { label: '场均助攻', home: '29.4', away: '25.2', homeValue: 54, awayValue: 46 },
    ],
    highlights: ['约基奇场均 26+12+9', '热火客场两连胜', '两队近 6 次交手各胜 3 场'],
  },
  {
    id: 'cba-xinjiang-beijing-20241023',
    league: 'cba',
    leagueLabel: 'CBA',
    round: 'CBA 2024-25 常规赛 · 第 6 轮',
    home: {
      name: '新疆伊力特',
      shortName: 'XJ',
      record: '4 胜 2 负',
      score: 103,
      quarters: [26, 24, 27, 26],
      starters: ['阿不都沙拉木（前锋）', '齐麟（后卫）', '于德豪（后卫）', '李炎哲（中锋）', '琼斯（后卫）'],
      coach: '邱彪',
    },
    away: {
      name: '北京首钢',
      shortName: 'BJ',
      record: '3 胜 3 负',
      score: 97,
      quarters: [24, 23, 25, 25],
      starters: ['翟晓川（前锋）', '范子铭（中锋）', '雷蒙（后卫）', '方硕（后卫）', '陈盈骏（后卫）'],
      coach: '许利民',
    },
    startTime: '2024-10-22T19:35:00+08:00',
    dateLabel: '10月22日 19:35',
    timeLabel: '10月22日 19:35',
    status: 'finished',
    progress: 100,
    quarter: '已结束',
    clock: '终场 103 - 97',
    venue: '乌鲁木齐红山体育馆',
    broadcaster: '篮球直播 · 录播回看',
    views: '42.7 万',
    summary:
      '新疆主场依靠第三节的 9-0 小高潮建立领先，阿不都沙拉木贡献 28 分 10 篮板。北京末节将分差追至 2 分，但连续两次进攻失误葬送机会。',
    cover:
      'https://readdy.ai/api/search-image?query=deep%20blue%20basketball%20backboard%20silhouette%20with%20warm%20orange%20backlight%20and%20soft%20smoke%20minimal%20flat%20sports%20graphic%20no%20text&width=1600&height=900&seq=bb-xj-bj-hero-08&orientation=landscape',
    stats: [
      { label: '投篮命中率', home: '47.9%', away: '45.1%', homeValue: 51, awayValue: 49 },
      { label: '三分命中', home: '11 / 29', away: '10 / 28', homeValue: 50, awayValue: 50 },
      { label: '篮板', home: '43', away: '38', homeValue: 53, awayValue: 47 },
      { label: '助攻', home: '22', away: '20', homeValue: 52, awayValue: 48 },
    ],
    highlights: ['阿不都沙拉木 28 分 10 篮板', '新疆第三节打出 9-0', '北京末节追至 2 分后连续失误'],
  },
  {
    id: 'fiba-asia-chn-jpn-20241026',
    league: 'international',
    leagueLabel: '国际赛',
    round: 'FIBA 亚洲杯预选赛 · 窗口期',
    home: {
      name: '中国男篮',
      shortName: 'CHN',
      record: '预选赛 3 胜 1 负',
      score: 0,
      quarters: [],
      starters: ['赵睿（后卫）', '胡金秋（中锋）', '张宁（前锋）', '廖三宁（后卫）', '曾凡博（前锋）'],
      coach: '郭士强',
    },
    away: {
      name: '日本男篮',
      shortName: 'JPN',
      record: '预选赛 2 胜 2 负',
      score: 0,
      quarters: [],
      starters: ['河村勇辉（后卫）', '富永启生（后卫）', '渡边雄太（前锋）', '霍金森（中锋）', '比江岛慎（后卫）'],
      coach: '汤姆·霍瓦斯',
    },
    startTime: '2024-10-28T19:00:00+08:00',
    dateLabel: '10月28日 19:00',
    timeLabel: '10月28日 19:00',
    status: 'upcoming',
    progress: 0,
    quarter: '未开始',
    clock: '10月28日 19:00 开赛',
    venue: '深圳大运中心',
    broadcaster: '篮球直播 · 官方信号',
    views: '预约 112.5 万',
    summary:
      '亚洲杯预选赛关键一战。中国队依靠内线身高与篮板控制压制对手，日本队则用速度与三分投射寻找机会。河村勇辉的突破与外线将是中国的防守重点。',
    cover:
      'https://readdy.ai/api/search-image?query=modern%20basketball%20hoop%20with%20orange%20net%20glow%20against%20dark%20navy%20gradient%20background%20with%20artistic%20light%20particles%20minimal%20sports%20design%20no%20text&width=1600&height=900&seq=bb-chn-jpn-hero-09&orientation=landscape',
    stats: [
      { label: '场均得分', home: '82.4', away: '78.9', homeValue: 51, awayValue: 49 },
      { label: '场均篮板', home: '42.8', away: '35.6', homeValue: 55, awayValue: 45 },
      { label: '场均助攻', home: '18.2', away: '21.4', homeValue: 46, awayValue: 54 },
      { label: '三分命中率', home: '33.6%', away: '36.9%', homeValue: 48, awayValue: 52 },
    ],
    highlights: ['中国队内线篮板优势明显', '日本队场均三分出手 32 次', '上次交手中国 9 分取胜'],
  },
  {
    id: 'nba-okc-dal-20241023',
    league: 'nba',
    leagueLabel: 'NBA',
    round: 'NBA 2024-25 常规赛 · 第 3 轮',
    home: {
      name: '俄克拉荷马雷霆',
      shortName: 'OKC',
      record: '3 胜 1 负',
      score: 118,
      quarters: [29, 31, 28, 30],
      starters: ['谢伊·吉尔杰斯-亚历山大（后卫）', '杰伦·威廉姆斯（前锋）', '切特·霍姆格伦（中锋）', '卢格恩茨·多尔特（后卫）', '以赛亚·哈尔滕施泰因（中锋）'],
      coach: '马克·戴格诺特',
    },
    away: {
      name: '达拉斯独行侠',
      shortName: 'DAL',
      record: '2 胜 2 负',
      score: 109,
      quarters: [26, 28, 27, 28],
      starters: ['卢卡·东契奇（后卫）', '凯里·欧文（后卫）', 'PJ·华盛顿（前锋）', '德里克·莱弗利（中锋）', '纳吉·马歇尔（前锋）'],
      coach: '杰森·基德',
    },
    startTime: '2024-10-22T09:00:00+08:00',
    dateLabel: '10月22日 09:00',
    timeLabel: '10月22日 09:00',
    status: 'finished',
    progress: 100,
    quarter: '已结束',
    clock: '终场 118 - 109',
    venue: '佩科姆中心',
    broadcaster: '篮球直播 · 录播回看',
    views: '73.9 万',
    summary:
      '雷霆年轻阵容全场送出 12 次抢断，亚历山大拿下 33 分 7 助攻。东契奇虽然砍下 30 分 11 篮板 9 助攻的三双数据，但独行侠末节三分 8 投 1 中，最终无力回天。',
    cover:
      'https://readdy.ai/api/search-image?query=abstract%20basketball%20motion%20swirls%20in%20orange%20and%20deep%20blue%20over%20dark%20background%20with%20soft%20grain%20modern%20sports%20visual%20no%20text&width=1600&height=900&seq=bb-okc-dal-hero-10&orientation=landscape',
    stats: [
      { label: '投篮命中率', home: '50.4%', away: '46.8%', homeValue: 52, awayValue: 48 },
      { label: '抢断', home: '12', away: '5', homeValue: 62, awayValue: 38 },
      { label: '篮板', home: '46', away: '42', homeValue: 52, awayValue: 48 },
      { label: '助攻', home: '27', away: '23', homeValue: 54, awayValue: 46 },
    ],
    highlights: ['亚历山大 33 分 7 助攻', '东契奇准三双', '雷霆全场 12 次抢断'],
  },
];